import { useEffect, useMemo, useRef, useState } from 'react';
import { FormControlLabel, Switch, useTheme } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import Autocomplete from '@/components/ui/Autocomplete/Autocomplete';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import {
    pricebookApi,
    type EstimateResult,
    type PricebookRule,
    type PricebookSearchParams,
} from '@/api/pricebook';
import { extractRules } from '@/helpers/pricebook';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';
import type { SearchRulesProps } from './ISearchRules';
import { styles } from './styles';

const SERVICE_TYPE_LOCKED = 'Keys';

const PAGE_SIZE = 25;

const fetchAllRules = async (
    params: PricebookSearchParams,
): Promise<PricebookRule[]> => {
    const firstPage = { ...params, page: 1, page_size: PAGE_SIZE };
    const first = await pricebookApi.searchPricebooks(firstPage);
    const firstRules = extractRules(first.data?.payload);
    const paging = first.data?.paging as
        | { total_count?: number; total?: number; page_size?: number }
        | undefined;
    const total = paging?.total_count ?? paging?.total ?? firstRules.length;
    const pageSize = paging?.page_size ?? PAGE_SIZE;
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return firstRules;
    const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
            pricebookApi.searchPricebooks({ ...params, page: i + 2, page_size: pageSize }),
        ),
    );
    const restRules = rest.flatMap((r) => extractRules(r.data?.payload));
    return [...firstRules, ...restRules];
};

const SearchRules = ({
    catalog,
    vehicleMakes,
    onLoadingChange,
    onSearchResults,
}: SearchRulesProps) => {
    const theme = useTheme();
    const errorCtx = useError();

    const [vin, setVin] = useState('');
    const [useVin, setUseVin] = useState(false);
    const [yearStart, setYearStart] = useState<number | null>(null);
    const [yearEnd, setYearEnd] = useState<number | null>(null);
    const [make, setMake] = useState<string[]>([]);
    const [model, setModel] = useState<string[]>([]);
    const [repairType, setRepairType] = useState('');
    const [part, setPart] = useState('');
    const [position, setPosition] = useState('');
    const [models, setModels] = useState<string[]>([]);
    const [decodedVehicle, setDecodedVehicle] =
        useState<EstimateResult['vehicle'] | null>(null);
    const [estimates, setEstimates] = useState<EstimateResult | null>(null);
    const initialSearchRef = useRef(false);

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from(
        { length: currentYear - 1979 + 2 },
        (_, i) => currentYear + 1 - i,
    );

    const repairTypeOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    catalog
                        .filter((c) => c.service_type === SERVICE_TYPE_LOCKED)
                        .map((c) => c.repair_type)
                        .filter(Boolean),
                ),
            ) as string[],
        [catalog],
    );
    const partOptions = useMemo(() => {
        let f = catalog.filter((c) => c.service_type === SERVICE_TYPE_LOCKED);
        if (repairType) f = f.filter((c) => c.repair_type === repairType);
        return Array.from(new Set(f.map((c) => c.part).filter(Boolean))) as string[];
    }, [catalog, repairType]);
    const positionOptions = useMemo(() => {
        let f = catalog.filter((c) => c.service_type === SERVICE_TYPE_LOCKED);
        if (repairType) f = f.filter((c) => c.repair_type === repairType);
        if (part) f = f.filter((c) => c.part === part);
        return Array.from(new Set(f.map((c) => c.position).filter(Boolean))) as string[];
    }, [catalog, repairType, part]);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const res = await pricebookApi.getVehicleModels(
                    make.length > 0 ? make : undefined,
                );
                if (cancelled) return;
                const list = res.data?.payload ?? [];
                setModels(list);
                if (model.length > 0) {
                    const trimmed = model.filter((m) => list.includes(m));
                    if (trimmed.length !== model.length) setModel(trimmed);
                }
            } catch {}
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [make, model]);

    const runSearch = async (
        overridePart?: string,
        overridePosition?: string,
    ): Promise<void> => {
        const params: PricebookSearchParams = {
            service_type: SERVICE_TYPE_LOCKED,
            include_description: false,
        };
        if (repairType) params.repair_type = repairType;
        if (useVin && vin) {
            params.vin = vin.trim();
        } else {
            if (yearStart) params.year_start = yearStart;
            if (yearEnd) params.year_end = yearEnd;
            if (make.length > 0) params.make = make;
            if (model.length > 0) params.model = model;
        }
        const partToUse = overridePart !== undefined ? overridePart : part;
        const positionToUse = overridePosition !== undefined ? overridePosition : position;
        if (partToUse) params.part = partToUse;
        if (positionToUse) params.position = positionToUse;

        onLoadingChange(true);
        try {
            const rules = await fetchAllRules(params);
            onSearchResults(rules, params);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Error searching pricebook';
            errorCtx.DISPATCH({ type: 'API_ERROR', payload: { alert: message } });
            onSearchResults([], params);
        } finally {
            onLoadingChange(false);
        }
    };

    useEffect(() => {
        if (initialSearchRef.current) return;
        initialSearchRef.current = true;
        void runSearch();
    }, []);

    const fetchEstimate = async (): Promise<void> => {
        if (!useVin || vin.length !== 17) return;
        onLoadingChange(true);
        try {
            const res = await pricebookApi.estimate({
                vehicle: { vin: vin.trim() },
                services: [
                    {
                        service_details: {
                            service_type: SERVICE_TYPE_LOCKED,
                            repair_type: repairType || undefined,
                        },
                    },
                ],
            });
            const result = res.data?.payload;
            if (!result) return;
            if (result.vehicle) {
                setDecodedVehicle({
                    year: result.vehicle.year,
                    make: result.vehicle.make,
                    model: result.vehicle.model,
                    vin: result.vehicle.vin ?? vin,
                });
            }
            const allEstimates = result.services?.flatMap((s) => s.estimates ?? []) ?? [];
            const partFromEstimate = allEstimates[0]?.service_details?.part ?? '';
            const uniquePositions = Array.from(
                new Set(
                    allEstimates
                        .map((e) => e.service_details?.position)
                        .filter(Boolean) as string[],
                ),
            );
            let nextPart = part;
            let nextPosition = position;
            if (partFromEstimate) {
                setPart(partFromEstimate);
                nextPart = partFromEstimate;
            }
            if (uniquePositions.length === 1) {
                setPosition(uniquePositions[0]);
                nextPosition = uniquePositions[0];
            } else if (uniquePositions.length > 1) {
                setPosition('');
                nextPosition = '';
            }
            setEstimates(result);
            errorCtx.DISPATCH({
                type: 'NO_ERROR',
                payload: { alert: 'VIN decoded successfully' },
            });
            await runSearch(nextPart, nextPosition);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Error decoding VIN';
            errorCtx.DISPATCH({ type: 'API_ERROR', payload: { alert: message } });
            setDecodedVehicle(null);
            setEstimates(null);
        } finally {
            onLoadingChange(false);
        }
    };

    useEffect(() => {
        if (useVin && vin.length === 17) void fetchEstimate();
    }, [vin, useVin]);

    const handleClear = (): void => {
        setVin('');
        setYearStart(null);
        setYearEnd(null);
        setMake([]);
        setModel([]);
        setRepairType('');
        setPart('');
        setPosition('');
        setUseVin(false);
        setDecodedVehicle(null);
        setEstimates(null);
        onLoadingChange(true);
        void fetchAllRules({
            service_type: SERVICE_TYPE_LOCKED,
            include_description: false,
        })
            .then((rules) =>
                onSearchResults(rules, {
                    service_type: SERVICE_TYPE_LOCKED,
                    include_description: false,
                }),
            )
            .finally(() => onLoadingChange(false));
    };

    return (
        <Box sx={styles.card(theme)}>
            <Typography sx={styles.title}>Search Pricebook Rules</Typography>
            <Box sx={styles.grid}>
                <Box sx={styles.full}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useVin}
                                onChange={(e) => {
                                    const next = e.target.checked;
                                    setUseVin(next);
                                    if (!next) {
                                        setVin('');
                                        setDecodedVehicle(null);
                                        setEstimates(null);
                                        setPart('');
                                        setPosition('');
                                    }
                                }}
                            />
                        }
                        label="Search by VIN"
                    />
                </Box>
                {useVin ? (
                    <>
                        <Box sx={styles.vinField}>
                            <FieldWrapper filled={Boolean(vin)} required>
                                <TextField
                                    fullWidth
                                    label="VIN"
                                    value={vin}
                                    placeholder="Enter 17-digit VIN"
                                    inputProps={{ maxLength: 17 }}
                                    onChange={(e) => {
                                        setVin(e.target.value.toUpperCase());
                                        setDecodedVehicle(null);
                                        setEstimates(null);
                                    }}
                                    helperText="Enter the vehicle identification number"
                                />
                            </FieldWrapper>
                        </Box>
                        {decodedVehicle && (
                            <Box sx={styles.decoded(theme)}>
                                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: 13 }}>
                                    Decoded Vehicle:
                                </Typography>
                                <Typography sx={{ fontSize: 13 }}>
                                    {decodedVehicle.year} {decodedVehicle.make}{' '}
                                    {decodedVehicle.model} (VIN: {decodedVehicle.vin})
                                </Typography>
                            </Box>
                        )}
                        {estimates?.services && estimates.services.length > 0 && (
                            <Box sx={styles.estimateBox(theme)}>
                                <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
                                    Estimates:
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontStyle: 'italic',
                                        color: 'grey.600',
                                        mb: 1,
                                    }}
                                >
                                    These estimates are from ALL companies, not just your
                                    pricebook.
                                </Typography>
                                {estimates.services.map((service, sIdx) =>
                                    (service.estimates ?? []).map((est, eIdx) => (
                                        <Box key={`${sIdx}-${eIdx}`} sx={{ mb: 1 }}>
                                            {est.service_details && (
                                                <Typography sx={{ fontWeight: 500, fontSize: 13 }}>
                                                    {est.service_details.service_type}
                                                    {est.service_details.repair_type
                                                        ? ` - ${est.service_details.repair_type}`
                                                        : ''}
                                                    {est.service_details.part
                                                        ? ` - ${est.service_details.part}`
                                                        : ''}
                                                    {est.service_details.position
                                                        ? ` (${est.service_details.position})`
                                                        : ''}
                                                </Typography>
                                            )}
                                            {(est.prices ?? []).map((p, pIdx) => (
                                                <Typography key={pIdx} sx={styles.estimateLine}>
                                                    • {p.price != null
                                                        ? `$${p.price.toFixed(2)}`
                                                        : 'Quote Required'}{' '}
                                                    — <strong>{p.name ?? 'Unknown Company'}</strong>
                                                    {p.rank != null ? ` (Rank: ${p.rank})` : ''}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )),
                                )}
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <FieldWrapper filled={Boolean(yearStart)}>
                            <Autocomplete<number>
                                options={yearOptions}
                                getOptionLabel={(o) => o.toString()}
                                value={yearStart}
                                onChange={(_, v) => setYearStart(v)}
                                label="Year Start"
                            />
                        </FieldWrapper>
                        <FieldWrapper filled={Boolean(yearEnd)}>
                            <Autocomplete<number>
                                options={yearOptions}
                                getOptionLabel={(o) => o.toString()}
                                value={yearEnd}
                                onChange={(_, v) => setYearEnd(v)}
                                label="Year End"
                            />
                        </FieldWrapper>
                        <FieldWrapper filled={make.length > 0}>
                            <Autocomplete<string, true, false, true>
                                multiple
                                freeSolo
                                options={vehicleMakes}
                                value={make}
                                onChange={(_, v) => setMake(v as string[])}
                                label="Make"
                            />
                        </FieldWrapper>
                        <FieldWrapper filled={model.length > 0}>
                            <Autocomplete<string, true, false, true>
                                multiple
                                freeSolo
                                options={models}
                                value={model}
                                onChange={(_, v) => setModel(v as string[])}
                                label="Model"
                            />
                        </FieldWrapper>
                    </>
                )}
                <FieldWrapper filled={Boolean(SERVICE_TYPE_LOCKED)} required>
                    <TextField fullWidth label="Service Type" value={SERVICE_TYPE_LOCKED} disabled />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(repairType)}>
                    <Autocomplete<string, false, false, true>
                        freeSolo
                        options={repairTypeOptions}
                        value={repairType || null}
                        onChange={(_, v) => {
                            setRepairType((v as string) ?? '');
                            setPart('');
                        }}
                        label="Repair Type"
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(part)}>
                    <Autocomplete<string, false, false, true>
                        freeSolo
                        options={partOptions}
                        value={part || null}
                        onChange={(_, v) => setPart((v as string) ?? '')}
                        label="Part"
                        disabled={useVin}
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(position)}>
                    <Autocomplete<string, false, false, true>
                        freeSolo
                        options={positionOptions}
                        value={position || null}
                        onChange={(_, v) => setPosition((v as string) ?? '')}
                        label="Position"
                        disabled={useVin}
                    />
                </FieldWrapper>
                <Box sx={styles.actions}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={() => void runSearch()}
                    >
                        Search
                    </Button>
                    <Button variant="outlined" onClick={handleClear}>
                        Clear
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default SearchRules;
